import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';
import { getWeaveinApiUrl, config } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { TERMS_OF_USE, CONSENT_VERSION } from '@/constants/legalTexts';

interface ConsentDocument {
  id: number;
  document_type: string;
  version: string;
  title: string;
  content: string;
  effective_date: string;
  is_active: boolean;
  summary?: string;
}

export default function TermsOfUsePage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [document, setDocument] = useState<ConsentDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [useApiContent, setUseApiContent] = useState(false);

  const handleBack = () => {
    // Check if there's history to go back to
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback to member home if no history
      navigate('/member/home');
    }
  };

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await fetch(
          `${getWeaveinApiUrl()}/api/consent/documents/terms_of_use${config.isTriveny ? '?product=triveny' : ''}`,
        );
        if (response.ok) {
          const data = await response.json();
          setDocument(data);
          // Only use API content if it's different from fallback
          setUseApiContent(data.content && data.content.length > 1000);
        }
      } catch (error) {
        console.error('Failed to fetch terms document, using fallback:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDocument();
  }, []);

  const effectiveDate = document?.effective_date
    ? new Date(document.effective_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'January 30, 2026';

  const version = document?.version || CONSENT_VERSION;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" className="text-teal-700 hover:text-teal-800" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('terms-of-use.back', 'Back')}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-teal-700" />
              </div>
              <div>
                <CardTitle className="text-2xl">{t('terms-of-use.terms_of_use', 'Terms of Service')}</CardTitle>
                <p className="text-sm text-gray-600">
                  Version {version} • Last updated: {effectiveDate}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
              </div>
            ) : useApiContent && document?.content ? (
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                {document.content}
              </pre>
            ) : (
              <>
                <h2>1. Acceptance of Terms</h2>
                <p>
                  By accessing and using the Harmony Healthcare Solutions (Trust) Platform ("Platform"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, please do not use the Platform.
                </p>

                <h2>2. Description of Service</h2>
                <p>
                  {t('terms-of-use.trustcare_provides_a_secure_webbased', 'Harmony Healthcare Solutions provides a secure web-based platform that allows you to:')}
                </p>
                <ul>
                  <li>{t('terms-of-use.view_your_care_coordination_services', 'View your care coordination services and referrals')}</li>
                  <li>{t('terms-of-use.communicate_with_your_care_team', 'Communicate with your care team and case managers')}</li>
                  <li>{t('terms-of-use.upload_and_manage_important_documents', 'Upload and manage important documents')}</li>
                  <li>{t('terms-of-use.track_your_progress_in_programs', 'Track your progress in programs and services')}</li>
                  <li>{t('terms-of-use.receive_notifications_and_updates_from', 'Receive notifications and updates from your care providers')}</li>
                </ul>

                <h2>3. User Account and Security</h2>
                <p>
                  {t('terms-of-use.to_use_the_portal_you', 'To use the Platform, you must create an account using a valid invitation from your care organization. You agree to:')}
                </p>
                <ul>
                  <li>{t('terms-of-use.provide_accurate_current_and_complete', 'Provide accurate, current, and complete information during registration')}</li>
                  <li>{t('terms-of-use.maintain_the_security_of_your', 'Maintain the security of your password and account')}</li>
                  <li>{t('terms-of-use.promptly_update_your_account_information', 'Promptly update your account information if it changes')}</li>
                  <li>{t('terms-of-use.accept_responsibility_for_all_activities', 'Accept responsibility for all activities that occur under your account')}</li>
                  <li>{t('terms-of-use.notify_trustcare_immediately_of_any', 'Notify Harmony Healthcare Solutions immediately of any unauthorized use of your account')}</li>
                </ul>

                <h2>4. Privacy and Data Protection</h2>
                <p>
                  Your privacy is important to us. We collect, use, and protect your personal health information in accordance with HIPAA regulations and our Privacy Policy. By using the Platform, you consent to the collection and use of your information as described in our Privacy Policy.
                </p>

                <h2>5. Permitted Use</h2>
                <p>
                  {t('terms-of-use.you_agree_to_use_the', 'You agree to use the Platform only for lawful purposes and in accordance with these Terms of Service. You agree NOT to:')}
                </p>
                <ul>
                  <li>{t('terms-of-use.use_the_portal_in_any', 'Use the Platform in any way that violates any applicable federal, state, or local law')}</li>
                  <li>{t('terms-of-use.share_your_account_credentials_with', 'Share your account credentials with others')}</li>
                  <li>{t('terms-of-use.attempt_to_gain_unauthorized_access', 'Attempt to gain unauthorized access to any portion of the Platform')}</li>
                  <li>{t('terms-of-use.upload_or_transmit_viruses_or', 'Upload or transmit viruses or any other malicious code')}</li>
                  <li>{t('terms-of-use.interfere_with_or_disrupt_the', 'Interfere with or disrupt the integrity or performance of the Platform')}</li>
                  <li>{t('terms-of-use.use_the_portal_for_any', 'Use the Platform for any commercial purpose without our express written consent')}</li>
                </ul>

                <h2>6. Content Ownership</h2>
                <p>
                  The Platform and its original content, features, and functionality are owned by Harmony Healthcare Solutions, Inc. and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
                </p>

                <h2>7. User-Generated Content</h2>
                <p>
                  You retain ownership of any content you submit, post, or display on or through the Platform ("User Content"). By submitting User Content, you grant Harmony Healthcare Solutions a non-exclusive, worldwide, royalty-free license to use, reproduce, and distribute such content solely for the purpose of providing care coordination services to you.
                </p>

                <h2>8. Third-Party Links</h2>
                <p>
                  The Platform may contain links to third-party websites or services that are not owned or controlled by Harmony Healthcare Solutions. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.
                </p>

                <h2>9. Disclaimer of Warranties</h2>
                <p>
                  {t('terms-of-use.the_portal_is_provided_on', 'THE PLATFORM IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. Harmony Healthcare Solutions does not warrant that:')}
                </p>
                <ul>
                  <li>{t('terms-of-use.the_portal_will_be_uninterrupted', 'The Platform will be uninterrupted, timely, secure, or error-free')}</li>
                  <li>{t('terms-of-use.the_results_obtained_from_using', 'The results obtained from using the Platform will be accurate or reliable')}</li>
                  <li>{t('terms-of-use.any_errors_in_the_portal', 'Any errors in the Platform will be corrected')}</li>
                </ul>

                <h2>10. Limitation of Liability</h2>
                <p>
                  IN NO EVENT SHALL HARMONY HEALTHCARE SOLUTIONS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE PLATFORM.
                </p>

                <h2>11. Emergency Situations</h2>
                <p>
                  <strong>THE PLATFORM IS NOT FOR EMERGENCIES.</strong> If you are experiencing a medical emergency, call 911 immediately. Do not use the Platform to communicate urgent medical needs. Messages sent through the Platform may not be read or responded to immediately.
                </p>

                <h2>12. Termination</h2>
                <p>
                  We may terminate or suspend your account and access to the Platform immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms of Service.
                </p>

                <h2>13. Changes to Terms</h2>
                <p>
                  We reserve the right to modify or replace these Terms of Service at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. Continued use of the Platform after any changes constitutes acceptance of the new Terms of Service.
                </p>

                <h2>14. Governing Law</h2>
                <p>
                  {t('terms-of-use.these_terms_shall_be_governed', 'These Terms shall be governed and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions.')}
                </p>

                <h2>15. Contact Information</h2>
                <p>
                  {t('terms-of-use.if_you_have_any_questions', 'If you have any questions about these Terms of Service, please contact us:')}
                </p>
                <ul>
                  <li>{t('terms-of-use.email', 'Email:')} <a href="mailto:support@weavein.ai" className="text-teal-600">support@weavein.ai</a></li>
                  <li>Mail: Harmony Healthcare Solutions, Inc., 5776 Stoneridge Mall Road, Suite 125, Pleasanton, CA 94544</li>
                </ul>

                <div className="bg-teal-50 border-l-4 border-teal-600 p-4 mt-8">
                  <p className="font-semibold text-teal-900">{t('terms-of-use.acceptance', 'Acceptance')}</p>
                  <p className="text-sm text-teal-800 mt-2">
                    {t('terms-of-use.by_clicking_i_accept_during', 'By clicking "I Accept" during registration or by using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.')}
                  </p>
                </div>

                <div className="text-center mt-8 pt-6 border-t">
                  <p className="text-sm text-gray-600">
                    © {new Date().getFullYear()} Harmony Healthcare Solutions, Inc. All rights reserved.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    HARMONY HEALTH DOES NOT PROVIDE MEDICAL SERVICES. IF YOU REQUIRE MEDICAL ATTENTION, PLEASE CALL YOUR REGULAR DOCTOR.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
